import React, { memo, useRef } from "react";
import { RCCDropDownItem, RCCGrid, RCCIcon, RCCPopover, RCCProgress, RCCTooltip } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import UserColumn from "src/components/DefaultColumnsList/User";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import formatNumber from "src/utils/formatNumber";
import ModalIniciatives from "src/components/ModalIniciatives";
import { IRefIniciativeModals } from "src/components/ModalIniciatives/interface";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import { useAuth } from "src/context/Auth/context";
import OverflowSpan, { StyledDivDescriptionAndAlertExclamation, styleInlineListKR } from "./styles";
import { IListKR } from "./interface";
import { MeasurementForm, MeasurementType } from "../../keyResults/interface";

const ListKR: React.FC<IListKR> = ({
	data,
	callbackOnDelete,
	callbackOnUpdate,
	openModalAddOrChangeKr,
	openModalDeleteKr,
	openModalPerformCheckIn,
	disabled
}) => {

	const {translate, currentLanguage} = useTranslation();
	const { administrator, superUser, user, checkIfHasSpecificPermission } = useAuth();

	const hasPermissionToChange = checkIfHasSpecificPermission("okr/panel", PermissionEnum.CREATE_UPDATE);
	const userIsResponsibleKr = (responsible: number) => responsible === user.efa005Id;
	const permisionKr = (responsible: number, permission?: number) => (userIsResponsibleKr(responsible) || (permission && permission > 1)) || administrator || superUser;

	const refModalIniciatives = useRef<IRefIniciativeModals>(null);

	const formatNumberWithPrefixOrSufix = (number: number, measurementType: MeasurementType) => {
		if(measurementType.toString() === MeasurementType.MILESTONE || measurementType.toString() === MeasurementType.PERCENTAGE)
			return `${formatNumber(number, currentLanguage)}%`;

		if(measurementType.toString() === MeasurementType.MONETARY_VALUE)
			return `${translate("R$")} ${formatNumber(number, currentLanguage)}`;

		return formatNumber(number, currentLanguage);
	};

	const colUser = (row: any) => (
		<UserColumn 
			name={row.okb001Efa005Nome}
			image={row.okb001Efa005Imagem}
		/>
	);

	const colValues = (row: any) => (
		<RCCGrid
			container
			item
			direction="column"
			justifyContent="space-between"
		>
			{row.okb001Valorinicial > 0 && (
				<RCCGrid
					container
					item
					alignItems="center"
				>
					<OverflowSpan>
						{translate("Valor inicial")}
						<RCCIcon 
							name="checkSquare"
						/>
					</OverflowSpan>
					{formatNumberWithPrefixOrSufix(row.okb001Valorinicial, row.okb001Tipomedicao)}
				</RCCGrid>
			)}
			<RCCGrid
				container
				item
				alignItems="center"
			>
				<OverflowSpan>
					{translate("Realizado")}
					<RCCIcon 
						name="checkCircle"
					/>
				</OverflowSpan>
				{formatNumberWithPrefixOrSufix(row.realizedValuesCheckIn + row.okb001Valorinicial, row.okb001Tipomedicao)}
			</RCCGrid>
			{row.okb001Formamedicao && row.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS && (
				<RCCGrid
					container
					item
					alignItems="center"
				>
					<OverflowSpan>
						{translate("Meta")}
						<RCCIcon 
							name="trophy"
						/>
					</OverflowSpan>
					{formatNumberWithPrefixOrSufix(row.okb001Meta, row.okb001Tipomedicao)}
				</RCCGrid>
			)}
		</RCCGrid>
	);

	const colPercentWithIcons = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
		>
			<RCCProgress 
				layout="circle"
				value={row.percent}
				color={row.colorGraph && row.colorGraph !== null && row.colorGraph !== "" ? `#${row.colorGraph}` : undefined}
				width="60px"
				styleInline=">span { font-size: 0.9rem !important; }"
			/>
		</RCCGrid>
	);

	const colOptions = (row: any) => (
		<>
			{row.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS && (
				<RCCTooltip
					color="grey"
					text={`${translate("Forma de medição")}: ${translate("Resultados Chave")}`}
				>
					<RCCIcon 
						name="key"
					/>
				</RCCTooltip>
			)}
			{row.okb001Formamedicao.toString() === MeasurementForm.INICIATIVES && (
				<RCCTooltip
					color="grey"
					text={`${translate("Forma de medição")}: ${translate("Iniciativas")}`}
				>
					<RCCIcon 
						name="list"
					/>
				</RCCTooltip>
			)}
			{row.okb001Formamedicao.toString() === MeasurementForm.PROJECTS && (
				<RCCTooltip
					color="grey"
					text={`${translate("Forma de medição")}: ${translate("Projetos")}`}
				>
					<RCCIcon 
						name="table"
					/>
				</RCCTooltip>
			)}
			{row.okb001Formamedicao.toString() === MeasurementForm.INDICATORS && (
				<RCCTooltip
					color="grey"
					text={`${translate("Forma de medição")}: ${translate("Indicadores")}`}
				>
					<RCCIcon 
						name="chartBar"
					/>
				</RCCTooltip>
			)}


			<OptionsColumn>	
				{(permisionKr(row.okb001Efa005Id) || !disabled) && (
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { if(!openModalAddOrChangeKr) return; openModalAddOrChangeKr(row.okb001Id, undefined, "", () => {
								if(!callbackOnUpdate)
									return;
					
								callbackOnUpdate(row.okb001Id);
							}); }}
						>
							<RCCIcon name="pencil" />
							{translate("Alterar")}
						</RCCDropDownItem>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { if(!openModalPerformCheckIn) return; openModalPerformCheckIn(row.okb001Id, row.okb001Titulo, row.okb001Tipomedicao, row.okb001Formamedicao, row.realizedValuesCheckIn, row.percent, row.colorGraph, row.okb001Valorinicial, row.okb001Meta, () => {
								if(!callbackOnUpdate)
									return;
					
								callbackOnUpdate(row.okb001Id);
							}); }}
						>
							<RCCIcon name="checkSquare" />
							{translate("Realizar Check In")}
						</RCCDropDownItem>
						
				
					</CheckPermission>
				)}		
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { if(!refModalIniciatives.current) return; refModalIniciatives.current.openModalIniciative({ idKr: row.okb001Id }, row.okb001Titulo, undefined, hasPermissionToChange && (permisionKr(row.okb001Efa005Id) || !disabled) ? 2 : 1 ); }}
				>
					<RCCIcon name="list" />
					{translate("Listar iniciativas")}
				</RCCDropDownItem>
				{(permisionKr(row.okb001Efa005Id) || !disabled) && (
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => { if(!openModalDeleteKr) return; openModalDeleteKr(row.okb001Id, row.okb001Titulo, () => {
								if(!callbackOnDelete)
									return;

								callbackOnDelete(row.okb001Id);
							}); }}
						>
							<RCCIcon name="trash" />
							{translate("Excluir")}
						</RCCDropDownItem>
					</CheckPermission>
				)}	
			</OptionsColumn>	
			
		</>
	);

	const colKr = (row: any) => (
		<RCCGrid
			container
			item
			alignItems="center"
			justifyContent="space-between"
		>
			<OverflowSpan title={row.okb001Titulo}>
				{row.okb001Titulo}
			</OverflowSpan>
			<StyledDivDescriptionAndAlertExclamation>
				<RCCGrid container item alignItems="center">
					{row.checkInFrequency && row.checkInFrequency === "Hoje" && (
						<RCCIcon name="exclamationTriangle" size={16} color="var(--yellow)" />
					)}
					{row.checkInFrequency && row.checkInFrequency.substring(0, 8) === "Atrasado" && (
						<RCCIcon name="exclamationTriangle" size={16} color="var(--red)"/>
					)}
					{row.checkInFrequency && row.checkInFrequency.substring(0, 7) === "Próximo" && (
						<RCCIcon name="exclamationTriangle" size={16} color="var(--yellow)" />
					)}
				</RCCGrid>
				{row.okb001Descricao !== null && row.okb001Descricao.trim().length > 0 && (
					<RCCPopover
						color="grey"
						header={{
							title: translate("Descrição")
						}}
						body={{
							text: row.okb001Descricao
						}}
						position="top"
					>
						<div className="infoCircleDescription-kr">
							<RCCIcon name="exclamationCircle" color="var(--grey)" size={16} />
						</div>
					</RCCPopover>
				)}
			</StyledDivDescriptionAndAlertExclamation>
		</RCCGrid>
	);
    

	return(
		<>
			<List 
				data={data}
				columns={[{
					idColumn: "okb001Titulo",
					nameColumn: translate("Resultado Chave"),
					xs: 12,
					sm: 5,
					justify: "left",
					cellRenderer: colKr
				},{
					idColumn: "okb001Efa005Nome",
					nameColumn: translate("Responsável"),
					xs: 12,
					sm: 2,
					justify: "left",
					cellRenderer: colUser
				},{
					idColumn: "valores",
					nameColumn: "",
					xs: 12,
					sm: 3,
					justify: "left",
					cellRenderer: colValues
				},
				{
					idColumn: "percent",
					nameColumn: translate("Percentual"),
					justify: "left",
					xs: 12,
					sm: 2,
					cellRenderer: colPercentWithIcons
				},
				{
					idColumn: "options",
					nameColumn: " ",
					styleInline: "position: absolute; right: -8px; bottom: 0; width: fit-content !important;",
					hideHeader: true,
					cellRenderer: colOptions
				}]}
				showHeader
				styleInline={styleInlineListKR}
			/>

			<ModalIniciatives ref={refModalIniciatives} onCallbackIniciatives={() => {}} />
		</>
	);};

export default memo(ListKR);
